import { useLazyQuery } from '@apollo/client'
import useAuth from '@nobia/zeus-components/lib/auth/useAuth'
import features from '@nobia/zeus-components/lib/features'
import PropTypes from 'prop-types'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { AssignedStoresQuery } from '../graphql/AssignedStoresQuery'

const getAssignedStore = (assignedStores, storeId) => {
  if (features.multiStore === true) {
    return (assignedStores || []).find(
      (s) => s?.store?.id === storeId
    )
  }
  return assignedStores?.[0]
}

const sortStoreByName = (a, b) => a?.storeName?.localeCompare(b?.storeName)

const readStorage = (storageKey) => {
  if (features.multiStore === true) {
    try {
      const item = localStorage.getItem(storageKey)
      const { id } = JSON.parse(item) || {}
      return id
      // eslint-disable-next-line no-empty
    } catch (err) {}
  }
  return null
}

const writeStorage = (storageKey, item) => {
  if (features.multiStore === true) {
    localStorage.setItem(storageKey, JSON.stringify(item))
  }
}

export const AccountContext = createContext({
  store: null,
  staffMember: null,
  stores: [],
  loading: false,
})

export const AccountProvider = ({
  storageKey = 'selected-store',
  children,
}) => {
  const { status } = useAuth()
  const [fetchAssignedStores, { data, loading }] =
    useLazyQuery(AssignedStoresQuery)
  const [localLoading, setLocalLoading] = useState(loading || !data)
  const [selectedStoreId, setSelectedStoreId] = useState(
    readStorage(storageKey)
  )

  useEffect(() => setLocalLoading(loading || !data), [loading, data])

  useEffect(() => {
    if (status === 'authenticated') {
      fetchAssignedStores({
        variables: { multiStore: features.multiStore === true },
      })
    }
  }, [status, fetchAssignedStores])

  useEffect(() => {
    if (selectedStoreId) {
      writeStorage(storageKey, { id: selectedStoreId })
    }
  }, [selectedStoreId, storageKey])

  const assignedStores = useMemo(() => {
    const customer = data?.me?.customer
    if (features.multiStore === true) {
      return [...(customer?.stores || [])]
    }
    return customer
      ? [{ store: customer.store, staffMember: customer.staff }]
      : []
  }, [data?.me?.customer])

  const sortedStores = useMemo(() => {
    const stores = [...(assignedStores || [])].map((store) => store.store)
    if (features.multiStore === true) {
      return stores.sort(sortStoreByName)
    }
    return stores
  }, [assignedStores])

  const handleSetStore = useCallback(
    (storeId) => {
      if (!storeId || !getAssignedStore(assignedStores, storeId)) {
        return
      }
      setSelectedStoreId(storeId)
    },
    [assignedStores]
  )

  const handleClearStore = useCallback(
    () => localStorage.removeItem(storageKey),
    [storageKey]
  )

  const value = useMemo(() => {
    const assignedStore = getAssignedStore(assignedStores, selectedStoreId)
    return {
      store: assignedStore?.store,
      staffMember: assignedStore?.staffMember,
      stores: sortedStores,
      loading: localLoading,
      setStore: handleSetStore,
      clearStore: handleClearStore,
    }
  }, [
    selectedStoreId,
    sortedStores,
    assignedStores,
    localLoading,
    handleSetStore,
    handleClearStore,
  ])

  return (
    <AccountContext.Provider value={value}>{children}</AccountContext.Provider>
  )
}

AccountProvider.propTypes = {
  storageKey: PropTypes.string,
  children: PropTypes.node.isRequired,
}

export const useAccountContext = () => useContext(AccountContext)
